import { useState } from "react";
import type { SuggestedField, SuggestedTable, SuggestedTemplate } from "../api";

type EditableField = SuggestedField & { enabled: boolean };
type EditableTable = SuggestedTable & { enabled: boolean };

type Props = {
  suggestion: SuggestedTemplate;
  onCancel: () => void;
  onSubmit: (template: {
    versie: number;
    velden: SuggestedField[];
    tabellen: SuggestedTable[];
  }) => Promise<void> | void;
  submitting?: boolean;
  submitLabel?: string;
};

export function TemplateEditor({
  suggestion,
  onCancel,
  onSubmit,
  submitting,
  submitLabel = "Type opslaan",
}: Props) {
  const [velden, setVelden] = useState<EditableField[]>(
    suggestion.velden.map((v) => ({ ...v, enabled: true }))
  );
  const [tabellen, setTabellen] = useState<EditableTable[]>(
    suggestion.tabellen.map((t) => ({ ...t, enabled: true }))
  );

  const updateVeld = (idx: number, patch: Partial<EditableField>) => {
    setVelden((prev) => prev.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };
  const removeVeld = (idx: number) => {
    setVelden((prev) => prev.filter((_, i) => i !== idx));
  };
  const addVeld = () => {
    setVelden((prev) => [
      ...prev,
      { veld: "", strategie: "regex-na-label", label: "", patroon: ".+", enabled: true },
    ]);
  };

  const updateTable = (idx: number, patch: Partial<EditableTable>) => {
    setTabellen((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };
  const removeTable = (idx: number) => {
    setTabellen((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    const payload = {
      versie: suggestion.versie,
      velden: velden
        .filter((v) => v.enabled && v.veld.trim() && v.label.trim())
        .map(({ enabled: _, ...rest }) => rest),
      tabellen: tabellen
        .filter((t) => t.enabled && t.tabel_naam.trim())
        .map(({ enabled: _, ...rest }) => rest),
    };
    await onSubmit(payload);
  };

  return (
    <div className="bg-white border rounded-lg p-5 space-y-5">
      <div>
        <h3 className="font-medium mb-1">Template-voorstel</h3>
        <p className="text-xs text-gray-500">
          Dit stelt de app voor op basis van de documentlayout. Klik dubbels weg, pas aan, of voeg
          handmatig toe. Opslaan maakt het nieuwe document-type aan.
        </p>
      </div>

      <section>
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium text-sm text-gray-700">Velden ({velden.filter((v) => v.enabled).length})</h4>
          <button type="button" onClick={addVeld} className="text-xs text-blue-600 hover:underline">
            + handmatig toevoegen
          </button>
        </div>
        <div className="space-y-2">
          {velden.map((v, idx) => (
            <div
              key={idx}
              className={`border rounded p-2 ${v.enabled ? "bg-white" : "bg-gray-50 opacity-60"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={v.enabled}
                  onChange={(e) => updateVeld(idx, { enabled: e.target.checked })}
                />
                <input
                  type="text"
                  value={v.veld}
                  onChange={(e) => updateVeld(idx, { veld: e.target.value })}
                  placeholder="veld-naam"
                  className="font-mono text-sm border rounded px-2 py-1 w-40"
                />
                <button
                  type="button"
                  onClick={() => removeVeld(idx)}
                  className="ml-auto text-xs text-red-500 hover:text-red-700"
                >
                  Verwijder
                </button>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-2 text-xs">
                <label className="text-gray-500">Label:</label>
                <input
                  type="text"
                  value={v.label}
                  onChange={(e) => updateVeld(idx, { label: e.target.value })}
                  className="border rounded px-2 py-1"
                />
                <label className="text-gray-500">Regex:</label>
                <input
                  type="text"
                  value={v.patroon}
                  onChange={(e) => updateVeld(idx, { patroon: e.target.value })}
                  className="font-mono border rounded px-2 py-1"
                />
                <label className="text-gray-500">Strategie:</label>
                <div className="text-xs text-gray-600 py-1">{v.strategie}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h4 className="font-medium text-sm text-gray-700 mb-2">
          Tabellen ({tabellen.filter((t) => t.enabled).length})
        </h4>
        <div className="space-y-2">
          {tabellen.map((t, idx) => (
            <div
              key={idx}
              className={`border rounded p-2 ${t.enabled ? "bg-white" : "bg-gray-50 opacity-60"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={t.enabled}
                  onChange={(e) => updateTable(idx, { enabled: e.target.checked })}
                />
                <input
                  type="text"
                  value={t.tabel_naam}
                  onChange={(e) => updateTable(idx, { tabel_naam: e.target.value })}
                  placeholder="tabel-naam"
                  className="font-mono text-sm border rounded px-2 py-1 w-40"
                />
                {t.confidence !== null && t.confidence !== undefined && (
                  <span className="text-xs text-gray-500">conf {t.confidence.toFixed(2)}</span>
                )}
                <button
                  type="button"
                  onClick={() => removeTable(idx)}
                  className="ml-auto text-xs text-red-500 hover:text-red-700"
                >
                  Verwijder
                </button>
              </div>
              <div className="text-xs text-gray-600">
                <span className="text-gray-500">Kolommen: </span>
                {t.kolommen.map((k, i) => (
                  <span
                    key={i}
                    className="inline-block bg-gray-100 rounded px-1.5 py-0.5 mr-1 font-mono"
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {suggestion.ongebruikt.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
            {suggestion.ongebruikt.length} ongebruikte anchors (handmatig toe te voegen)
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {suggestion.ongebruikt.map((a) => (
              <span key={a} className="bg-gray-100 rounded px-2 py-0.5 text-xs">
                {a}
              </span>
            ))}
          </div>
        </details>
      )}

      <div className="flex gap-2 pt-3 border-t">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded bg-blue-600 text-white px-4 py-2 text-sm disabled:opacity-50 hover:bg-blue-700"
        >
          {submitting ? "Bewaren…" : submitLabel}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          className="rounded border px-4 py-2 text-sm hover:bg-gray-50"
        >
          Annuleer
        </button>
      </div>
    </div>
  );
}
